import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { isCollegeChallengeEnabled } from '../utils/collegeChallengeAccess';
import { NextGenAuthProvider } from '../NextGenBuilder/context/NextGenAuthContext';
import lazyWithRetry from '../utils/lazyWithRetry';

// Every page below is its own chunk: a visitor downloads only the route they
// open (plus shared vendor code), not the whole app. <Suspense> for these
// lives in App.js, around <AppRoutes />.
const Dashboard = lazyWithRetry(() => import('../BasicComponents/Dashboard'));
const Contact = lazyWithRetry(() => import('../BasicComponents/Contact'));
const Register = lazyWithRetry(() => import('../BasicComponents/Register'));
const Login = lazyWithRetry(() => import('../BasicComponents/Login'));
const Home = lazyWithRetry(() => import('../BasicComponents/Home'));
const About = lazyWithRetry(() => import('../BasicComponents/About'));
const TsgVsYoutube = lazyWithRetry(() => import('../BasicComponents/compare/TsgVsYoutube'));
const TsgVsEdtech = lazyWithRetry(() => import('../BasicComponents/compare/TsgVsEdtech'));
const TsgVsCoaching = lazyWithRetry(() => import('../BasicComponents/compare/TsgVsCoaching'));
const ResetPassword = lazyWithRetry(() => import('../BasicComponents/ResetPassword'));
const ForgotPassword = lazyWithRetry(() => import('../BasicComponents/ForgotPassword'));
const LockedAccount = lazyWithRetry(() => import('../BasicComponents/LockedAccount'));
const CleanProfile = lazyWithRetry(() => import('../BasicComponents/CleanProfile'));
const EditProfile = lazyWithRetry(() => import('../BasicComponents/EditProfile'));
const LevelTree = lazyWithRetry(() => import('../BasicComponents/SkillTree'));
const Leaderboard = lazyWithRetry(() => import('../BasicComponents/Leaderboard'));
const LevelClearance = lazyWithRetry(() => import('../BasicComponents/LevelClearance'));
const FlipCardList = lazyWithRetry(() => import('../BasicComponents/FlipCardList'));
const AdminDashboard = lazyWithRetry(() => import('../BasicComponents/AdminDashboard'));
const QuestionPage = lazyWithRetry(() => import('../BasicComponents/QuestionPage'));
const TechGrid = lazyWithRetry(() => import('../AdminComponents/TechGrid'));
const Python = lazyWithRetry(() => import('../AdminComponents/Python'));
const HuntLevel1 = lazyWithRetry(() => import('../CodeHunt/HuntLevel1'));
const HuntLevel2 = lazyWithRetry(() => import('../CodeHunt/HuntLevel2'));
const HuntLevel3 = lazyWithRetry(() => import('../CodeHunt/HuntLevel3'));
const UserTypeSelector = lazyWithRetry(() => import('../BasicComponents/UserTypeSelector'));
const CollegeRegister = lazyWithRetry(() => import('../BasicComponents/CollegeRegister'));
const CollegeIntroPage = lazyWithRetry(() => import('../BasicComponents/CollegeIntroPage'));
const CollegeIntroRedirect = lazyWithRetry(() => import('../BasicComponents/collegeIntro/CollegeIntroRedirect'));
const StudentIntroPage = lazyWithRetry(() => import('../BasicComponents/StudentIntroPage'));
const StudentIntroRedirect = lazyWithRetry(() => import('../BasicComponents/studentIntro/StudentIntroRedirect'));
const SkillgenLaunchpad = lazyWithRetry(() => import('../AdminComponents/SkillgenLaunchpad'));
const TrackQuestionPage = lazyWithRetry(() => import('../BasicComponents/TrackQuestionPage'));
const CollegeHomePage = lazyWithRetry(() => import('../BasicComponents/CollegeHomePage'));
const CollegeViewProfile = lazyWithRetry(() => import('../BasicComponents/CollegeViewProfile'));
const CollegeEditProfile = lazyWithRetry(() => import('../BasicComponents/CollegeEditProfile'));
const CollegeTotalStudents = lazyWithRetry(() => import('../BasicComponents/CollegeTotalStudents'));
const CollegeStudentsProgress = lazyWithRetry(() => import('../BasicComponents/CollegeStudentsProgress'));
const CollegeTopPerformers = lazyWithRetry(() => import('../BasicComponents/CollegeTopPerformers'));
const CollegeFAQs = lazyWithRetry(() => import('../BasicComponents/CollegeFAQs'));
const CollegeFeedbackInbox = lazyWithRetry(() => import('../BasicComponents/CollegeFeedbackInbox'));
const CollegeLogoutFromThisDevice = lazyWithRetry(() => import('../BasicComponents/CollegeLogoutFromThisDevice'));
const CollegeLogoutFromAllDevices = lazyWithRetry(() => import('../BasicComponents/CollegeLogoutFromAllDevices'));
const CollegeCreditPurchase = lazyWithRetry(() => import('../BasicComponents/CollegeCreditPurchase'));
const InstitutionalImpact = lazyWithRetry(() => import('../BasicComponents/InstitutionalImpact'));
const Notes = lazyWithRetry(() => import('../BasicComponents/Notes'));
const ContestPage = lazyWithRetry(() => import('../BasicComponents/ContestPage'));
const Collegelogin = lazyWithRetry(() => import('../BasicComponents/Collegelogin'));
const LaunchpadPage = lazyWithRetry(() => import('../BasicComponents/LaunchpadPage'));
const SkillgenLaunchpadQuestionPage = lazyWithRetry(() => import('../BasicComponents/SkillgenLaunchpadQuestionPage'));
const SkillgenLaunchpadLevels = lazyWithRetry(() => import('../BasicComponents/SkillgenLaunchpadLevels'));
const SkillgenLaunchpadLevelClearance = lazyWithRetry(() => import('../BasicComponents/SkillgenLaunchpadLevelClearance'));
const SupportTicketDashboard = lazyWithRetry(() => import('../AdminComponents/SupportTicketDashboard'));
const AdminUsersDashboard = lazyWithRetry(() => import('../AdminComponents/AdminUsersDashboard'));
const AdminCollegesDashboard = lazyWithRetry(() => import('../AdminComponents/AdminCollegesDashboard'));
const AdminTechnologySelector = lazyWithRetry(() => import('../AdminComponents/AdminTechnologySelector'));
const AdminQuestionManager = lazyWithRetry(() => import('../AdminComponents/AdminQuestionManager'));
const SkillGenPaymentPage = lazyWithRetry(() => import('../BasicComponents/SkillGenPaymentPage'));
const SkillgenproCompiler = lazyWithRetry(() => import('../components/SkillgenproCompiler'));
const LaunchpadReader = lazyWithRetry(() => import('../components/LaunchpadReader'));
const FeaturesOverview = lazyWithRetry(() => import('../BasicComponents/FeaturesOverview'));
const TechTreeReader = lazyWithRetry(() => import('../components/TechTreeReader'));
const SupportStudent = lazyWithRetry(() => import('../BasicComponents/SupportStudent'));
const SupportCollege = lazyWithRetry(() => import('../BasicComponents/SupportCollege'));
const LandingPage1 = lazyWithRetry(() => import('../BasicComponents/LandingPage1'));
const LandingPage2 = lazyWithRetry(() => import('../BasicComponents/LandingPage2'));
const PublicBookDemo = lazyWithRetry(() => import('../BasicComponents/PublicBookDemo'));

//compiler pages
const LanguageGrid = lazyWithRetry(() => import('../BasicComponents/LanguageGrid'));
const PracticeLevelPage = lazyWithRetry(() => import('../BasicComponents/PracticeLevelPage'));
const SolveCodingQuestionPage = lazyWithRetry(() => import('../BasicComponents/SolveCodingQuestionPage'));
const Compiler = lazyWithRetry(() => import('../BasicComponents/Compiler'));
const BootcampSelector = lazyWithRetry(() => import('../BasicComponents/learnerBootcampSelector'));
const ThankYouPage = lazyWithRetry(() => import('../BasicComponents/ThankYouPage'));
const Collegeforgotpassword = lazyWithRetry(() => import('../BasicComponents/collegeforgotpassword'));
const Collegeresetpassword = lazyWithRetry(() => import('../BasicComponents/collegeresetpassword'));
const LaunchpadHome = lazyWithRetry(() => import('../BasicComponents/LaunchpadHome'));
const TopicContent = lazyWithRetry(() => import('../BasicComponents/TopicContent'));
const CollegeDashboard = lazyWithRetry(() => import('../BasicComponents/CollegeDashboard'));
const CollegeAnalyticsDashboard = lazyWithRetry(() => import('../CollegeComponents/CollegeAnalyticsDashboard'));
const CollegeChallenge = lazyWithRetry(() => import('../CollegeComponents/CollegeChallenge'));
const TopicExplorer = lazyWithRetry(() => import('../components/TopicExplorer'));
const Bootcamp1 = lazyWithRetry(() => import('../components/learnerbootcamp1'));
const TenantBootcampEnrollment = lazyWithRetry(() => import('../TenantDashboard/TenantBootcampEnrollment'));
const UnifiedBootcampDashboard = lazyWithRetry(() => import('../components/learnerUnifiedBootcampDashboard'));
const Technologyuserdashboard = lazyWithRetry(() => import('../components/learnertechnologyuserdashboard'));
const Techbunchuserdashboard = lazyWithRetry(() => import('../components/learnertechbunchuserdashboard'));
const Certifieduserdashboard = lazyWithRetry(() => import('../components/learnercertifieduserdashboard'));

// ✅ Imported for Markdown Page
// import Sidebar1 from"../components/Sidebar1";
// import MarkdownViewer from"../components/MarkdownViewer";
// import ThemeToggle from"../components/ThemeToggle";
// import TopicExplorer from"../components/TopicExplorer";
const BulkEmailing = lazyWithRetry(() => import('../SuperAdminComponents/BulkEmailing'));
const SuperPayments = lazyWithRetry(() => import('../SuperAdminComponents/SuperPayments'));
const SuperAdminLayout = lazyWithRetry(() => import('../SuperAdminComponents/SuperAdminLayout'));
const TeeGeeLeads = lazyWithRetry(() => import('../SuperAdminComponents/TeeGeeLeads'));
const EliteTechnologyManager = lazyWithRetry(() => import('../SuperAdminComponents/EliteTechnologyManager'));
const EliteContentWorkspace = lazyWithRetry(() => import('../SuperAdminComponents/content/EliteContentWorkspace'));
const ProjectsList = lazyWithRetry(() => import('../SuperAdminComponents/NextGenBuilder/ProjectsList'));
const ProjectDetails = lazyWithRetry(() => import('../SuperAdminComponents/NextGenBuilder/ProjectDetails'));
const VersionDetails = lazyWithRetry(() => import('../SuperAdminComponents/NextGenBuilder/VersionDetails'));
const RolesManagement = lazyWithRetry(() => import('../SuperAdminComponents/NextGenBuilder/RolesManagement'));
const TemplatesManagement = lazyWithRetry(() => import('../SuperAdminComponents/NextGenBuilder/TemplatesManagement'));
// Both of these screens already existed and are already linked from the
// NextGen Builder header (ProjectsList.jsx:237 and :253) — they were simply
// never imported or given a <Route>, so every click landed on the SPA's
// catch-all 404 rather than the page that was sitting there ready to render.
const GitHubAccess = lazyWithRetry(() => import('../SuperAdminComponents/NextGenBuilder/GitHubAccess'));
const NextGenFinance = lazyWithRetry(() => import('../SuperAdminComponents/NextGenBuilder/NextGenFinance'));
const LeadAmbassadorDashboard = lazyWithRetry(() => import('../AdminComponents/LeadAmbassadorDashboard'));
const TechExplorerHome = lazyWithRetry(() => import('../BasicComponents/TechExplorerHome'));
const TechnologyDetail = lazyWithRetry(() => import('../BasicComponents/TechnologyDetail'));
const TSGMyNotes = lazyWithRetry(() => import('../BasicComponents/Tsgmynotes'));
const TSG = lazyWithRetry(() => import('../components/TSG'));
const MyCertifications = lazyWithRetry(() => import('../pages/student/MyCertifications'));
const NotFound = lazyWithRetry(() => import('../BasicComponents/NotFound'));
const Adminroles = lazyWithRetry(() => import('../AdminComponents/adminroles'));
const AdminProjectTeams = lazyWithRetry(() => import('../components/AdminProjectTeam'));
const IndividualRoles = lazyWithRetry(() => import('../components/IndividualRoles'));
const TeamRoles = lazyWithRetry(() => import('../components/TeamRoles'));
const TeamLeaderboardPage = lazyWithRetry(() => import('../components/TeamLeaderboardPage'));
const PaymentPage = lazyWithRetry(() => import('../components/PaymentPage'));
const PaymentSuccess = lazyWithRetry(() => import('../components/PaymentSuccess'));
const SubscriptionGuard = lazyWithRetry(() => import('../components/SubscriptionGuard'));

// NextGen Builder Components
const NextGenRegister = lazyWithRetry(() => import('../NextGenBuilder/pages/NextGenRegister'));
const NextGenLogin = lazyWithRetry(() => import('../NextGenBuilder/pages/NextGenLogin'));
const NextGenForgotPassword = lazyWithRetry(() => import('../NextGenBuilder/pages/NextGenForgotPassword'));
const NextGenResetPassword = lazyWithRetry(() => import('../NextGenBuilder/pages/NextGenResetPassword'));
const NextGenVerifyEmail = lazyWithRetry(() => import('../NextGenBuilder/pages/NextGenVerifyEmail'));
const NextGenUnsubscribe = lazyWithRetry(() => import('../NextGenBuilder/pages/NextGenUnsubscribe'));
const NextGenNotificationSettings = lazyWithRetry(() => import('../NextGenBuilder/pages/NextGenNotificationSettings'));
const NextGenDashboard = lazyWithRetry(() => import('../NextGenBuilder/pages/NextGenDashboard'));
const NextGenProjectVersion = lazyWithRetry(() => import('../NextGenBuilder/pages/NextGenProjectVersion'));
const NextGenEnrollment = lazyWithRetry(() => import('../NextGenBuilder/pages/NextGenEnrollment'));
const NextGenProfile = lazyWithRetry(() => import('../NextGenBuilder/pages/NextGenProfile'));
const NextGenPurchase = lazyWithRetry(() => import('../NextGenBuilder/pages/NextGenPurchase'));
const NextGenPayment = lazyWithRetry(() => import('../NextGenBuilder/pages/NextGenPayment'));
const NextGenPayments = lazyWithRetry(() => import('../NextGenBuilder/pages/NextGenPayments'));
// TSG Nexus feature-based billing (26-Aug-2026)
const NextGenFeaturePlans = lazyWithRetry(() => import('../NextGenBuilder/pages/NextGenFeaturePlans'));
const NextGenCart = lazyWithRetry(() => import('../NextGenBuilder/pages/NextGenCart'));
const NextGenCheckout = lazyWithRetry(() => import('../NextGenBuilder/pages/NextGenCheckout'));
const NextGenOrderDetail = lazyWithRetry(() => import('../NextGenBuilder/pages/NextGenOrderDetail'));
const NexusBillingAdmin = lazyWithRetry(() => import('../SuperAdminComponents/NexusBillingAdmin'));
const FeaturePricingAdmin = lazyWithRetry(() => import('../SuperAdminComponents/FeaturePricingAdmin'));
const PushNudgesAdmin = lazyWithRetry(() => import('../SuperAdminComponents/PushNudgesAdmin'));
const AnnouncementsAdmin = lazyWithRetry(() => import('../SuperAdminComponents/AnnouncementsAdmin'));
const NextGenReferral = lazyWithRetry(() => import('../NextGenBuilder/pages/NextGenReferral'));
const NextGenWallet = lazyWithRetry(() => import('../NextGenBuilder/pages/NextGenWallet'));
const SupportNextGen = lazyWithRetry(() => import('../NextGenBuilder/pages/SupportNextGen'));
const NextGenLayout = lazyWithRetry(() => import('../NextGenBuilder/components/NextGenLayout'));
const NextGenAuthThemeProvider = lazyWithRetry(() => import('../NextGenBuilder/components/NextGenAuthThemeProvider'));
const NextGenPaidFeatureGuard = lazyWithRetry(() => import('../NextGenBuilder/components/NextGenPaidFeatureGuard'));
const NextGenAITutorHub = lazyWithRetry(() => import('../NextGenBuilder/pages/NextGenAITutorHub'));
const SubscriptionPlans = lazyWithRetry(() => import('../components/SubscriptionPlans'));
const RoleDashboard = lazyWithRetry(() => import('../components/RoleDashboard'));
const IndividualRoleViewer = lazyWithRetry(() => import('../components/IndividualRoleViewer'));
const TeamRoleReplica = lazyWithRetry(() => import('../components/TeamRoleReplica'));
const CertificatePage = lazyWithRetry(() => import('../components/CertificatePage'));
const CertificateSystemDemo = lazyWithRetry(() => import('../components/CertificateSystemDemo'));
const CertificatePreview = lazyWithRetry(() => import('../components/CertificatePreview'));
const VoiceTrainingPage = lazyWithRetry(() => import('../pages/VoiceTrainingPage'));

// Mock Interview pages
const CodingInterviewPage = lazyWithRetry(() => import('../components/MockInterview/CodingInterviewPage'));
const MockTestPage = lazyWithRetry(() => import('../components/MockInterview/MockTestPage'));
const MockResultsPage = lazyWithRetry(() => import('../components/MockInterview/MockResultsPage'));
const MockInterviewHome = lazyWithRetry(() => import('../components/MockInterview/MockInterviewHome'));
const PremiumInterviewView = lazyWithRetry(() => import('../components/Interview/PremiumInterviewView'));
// import DiscountInterviewView from '../components/Interview/DiscountInterviewView';
const CodingInterviewView = lazyWithRetry(() => import('../components/Interview/CodingInterviewView'));
const TechnicalInterviewRoom = lazyWithRetry(() => import('../components/MockInterview/TechnicalInterviewRoom'));
const HRInterviewRoom = lazyWithRetry(() => import('../components/MockInterview/HRInterviewRoom'));
const MockInterviewResult = lazyWithRetry(() => import('../components/MockInterview/MockInterviewResult'));
const Timemanagequestionsmock = lazyWithRetry(() => import('../components/MockInterview/Timemanagequestionsmock'));
const MockInterviewGrid = lazyWithRetry(() => import('../components/MockInterview/MockInterviewGrid'));
const MockInterviewPayments = lazyWithRetry(() => import('../components/MockInterview/MockInterviewPayments'));
const MockInterviewMyPaymentsHistory = lazyWithRetry(() => import('../components/MockInterview/MockInterviewMyPaymentsHistory'));
const TSGNotes = lazyWithRetry(() => import('../BasicComponents/tsgnotes'));
const TSGPromptPage = lazyWithRetry(() => import('../components/TSGPromptPage'));
const ExplainPage = lazyWithRetry(() => import('../components/ExplainPage'));
const ResumeReviewPage = lazyWithRetry(() => import('../components/ResumeReviewPage'));
const InterviewPage = lazyWithRetry(() => import('../components/InterviewPage'));
const CodeReviewPage = lazyWithRetry(() => import('../components/CodeReviewPage'));
const SkillTestPage = lazyWithRetry(() => import('../components/SkillTestPage'));
const ProjectBuilderPage = lazyWithRetry(() => import('../components/ProjectBuilderPage'));
// import VoiceAssistant from '../BasicComponents/VoiceAssistant';
const TermsOfService = lazyWithRetry(() => import('../components/TermsOfService'));
const PrivacyPolicy = lazyWithRetry(() => import('../components/PrivacyPolicy'));
const AccountDeletion = lazyWithRetry(() => import('../components/AccountDeletion'));
const CollegeStudentManagement = lazyWithRetry(() => import('../BasicComponents/CollegeStudentManagement'));
const TenantManagement = lazyWithRetry(() => import('../SuperAdminComponents/TenantManagement'));
const CertificateVerification = lazyWithRetry(() => import('../components/Certificate/CertificateVerification'));
const CollegeCertificateManagement = lazyWithRetry(() => import('../BasicComponents/CollegeCertificateManagement'));
const TenantPortalHome = lazyWithRetry(() => import('../BasicComponents/TenantPortalHome'));
const TenantAdminLogin = lazyWithRetry(() => import('../BasicComponents/TenantAdminLogin'));
const TenantStudentLogin = lazyWithRetry(() => import('../BasicComponents/TenantStudentLogin'));

// ── Tenant Student Dashboard ─────────────────────────────────────────────────
const TenantStudentAuthGuard = lazyWithRetry(() => import('../TenantDashboard/TenantStudentAuthGuard'));
const TenantDashboardLayout = lazyWithRetry(() => import('../TenantDashboard/TenantDashboardLayout'));
const TenantDashboardHome = lazyWithRetry(() => import('../TenantDashboard/TenantDashboardHome'));
const MyBootcamps = lazyWithRetry(() => import('../TenantDashboard/MyBootcamps'));
const TenantGitHubProfile = lazyWithRetry(() => import('../TenantDashboard/TenantGitHubProfile'));
const CollegeGitHubDashboard = lazyWithRetry(() => import('../BasicComponents/CollegeGitHubDashboard'));
const CollegeTransactionHistory = lazyWithRetry(() => import('../BasicComponents/CollegeTransactionHistory'));
const EnterprisePurchasePage = lazyWithRetry(() => import('../BasicComponents/EnterprisePurchasePage'));
const EnterprisePaymentsAdmin = lazyWithRetry(() => import('../SuperAdminComponents/EnterprisePaymentsAdmin'));
const SalesTeamManagedPage = lazyWithRetry(() => import('../BasicComponents/SalesTeamManagedPage'));

const AdminLayout = lazyWithRetry(() => import('../BasicComponents/AdminLayout'));

// CRM Module (Super Admin only)
const CRMDashboard = lazyWithRetry(() => import('../SuperAdminComponents/crm/CRMDashboard'));
const CRMContacts = lazyWithRetry(() => import('../SuperAdminComponents/crm/CRMContacts'));
const CRMBulkUpload = lazyWithRetry(() => import('../SuperAdminComponents/crm/CRMBulkUpload'));
const CRMSMS = lazyWithRetry(() => import('../SuperAdminComponents/crm/CRMSMS'));
const CRMWhatsApp = lazyWithRetry(() => import('../SuperAdminComponents/crm/CRMWhatsApp'));
const CRMVoiceCalling = lazyWithRetry(() => import('../SuperAdminComponents/crm/CRMVoiceCalling'));
const CRMScheduleDemo = lazyWithRetry(() => import('../SuperAdminComponents/crm/CRMScheduleDemo'));
const CRMDemoCalendar = lazyWithRetry(() => import('../SuperAdminComponents/crm/CRMDemoCalendar'));
const CRMCommunicationHistory = lazyWithRetry(() => import('../SuperAdminComponents/crm/CRMCommunicationHistory'));
const CRMAnalytics = lazyWithRetry(() => import('../SuperAdminComponents/crm/CRMAnalytics'));
const CRMSettings = lazyWithRetry(() => import('../SuperAdminComponents/crm/CRMSettings'));

// Tenant feature wrappers
const TenantMockInterviewHome = lazyWithRetry(() => import('../TenantDashboard/features/TenantMockInterviewHome'));
const TenantTechExplorerHome = lazyWithRetry(() => import('../TenantDashboard/features/TenantTechExplorerHome'));
const TenantTechnologyDetail = lazyWithRetry(() => import('../TenantDashboard/features/TenantTechnologyDetail'));
const TenantLaunchpadHome = lazyWithRetry(() => import('../TenantDashboard/features/TenantLaunchpadHome'));
const TenantLaunchpadContent = lazyWithRetry(() => import('../TenantDashboard/features/TenantLaunchpadContent'));
const TenantTSG = lazyWithRetry(() => import('../TenantDashboard/features/TenantTSG'));
const TenantTSGPromptPage = lazyWithRetry(() => import('../TenantDashboard/features/TenantTSGPromptPage'));

// Elite Technology Mastery
const EliteTechnologyMastery = lazyWithRetry(() => import('../components/EliteTechnologyMastery'));
const EliteTechnologyDetail = lazyWithRetry(() => import('../components/EliteTechnologyDetail'));

// Detect if the current hostname is a tenant subdomain
// e.g. svecw.theskillgenie.com → true; theskillgenie.com → false
const BASE_DOMAIN = process.env.REACT_APP_BASE_DOMAIN || 'theskillgenie.com';

function isTenantSubdomain() {
  const hostname = window.location.hostname.toLowerCase();
  return (
    hostname !== BASE_DOMAIN &&
    hostname !== `www.${BASE_DOMAIN}` &&
    hostname !== 'localhost' &&
    !hostname.startsWith('127.') &&
    hostname.endsWith(`.${BASE_DOMAIN}`)
  );
}

/**
 * CollegeChallengeGuard
 *
 * Protects the /college-challenge route from being accessed by colleges
 * that are not among the first 100 registrants.
 *
 * Ineligible colleges are silently redirected to their dashboard, preventing
 * blank screens and infinite redirect loops.
 */
const CollegeChallengeGuard = ({ children }) => {
  if (isCollegeChallengeEnabled()) {
    return children;
  }
  // Redirect to the college dashboard (or root if no user stored)
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const username = user?.username;
  return <Navigate to={username ? `/collegehome/${username}` : '/'} replace />;
};

const AppRoutes = ({ chatBotRef, setShowChat }) => {
  const onTenantSubdomain = isTenantSubdomain();

  // ── TENANT PORTAL ROUTES ────────────────────────────────────────────
  // Shown when visiting a tenant subdomain (e.g. svecw.theskillgenie.com)
  // Shared routes (dashboard, college admin pages) remain accessible on both.
  if (onTenantSubdomain) {
    return (
      <Routes>
        {/* Tenant portal landing page */}
        <Route path="/" element={<TenantPortalHome />} />

        <Route path="/student/login" element={<TenantStudentLogin />} />
        <Route path="/admin/login" element={<TenantAdminLogin />} />

        {/* Catch-all for common learner routes on tenant subdomain — redirect to /dashboard */}
        <Route path="/mockinterviewhome" element={<Navigate to="/dashboard" replace />} />
        <Route path="/explore" element={<Navigate to="/dashboard" replace />} />
        <Route path="/launch" element={<Navigate to="/dashboard" replace />} />
        <Route path="/tsg" element={<Navigate to="/dashboard" replace />} />

        {/* Admin dashboard routes (same components, accessible from tenant domain) */}
        <Route path="/admin/dashboard" element={<CollegeDashboard />} />
        <Route path="/collegeabout" element={<CollegeDashboard />} />
        <Route path="/college-analytics" element={<CollegeAnalyticsDashboard />} />
        <Route path="/CollegeViewProfile" element={<CollegeViewProfile />} />
        <Route path="/CollegeEditProfile" element={<CollegeEditProfile />} />
        <Route path="/TotalStudents" element={<CollegeTotalStudents />} />
        <Route path="/StudentsProgress" element={<CollegeStudentsProgress />} />
        <Route path="/TopPerformers" element={<CollegeTopPerformers />} />
        <Route path="/CollegeFAQs" element={<CollegeFAQs />} />
        <Route path="/CollegeFeedback" element={<CollegeFeedbackInbox />} />
        <Route path="/CollegeLogoutFromThisDevice" element={<CollegeLogoutFromThisDevice />} />
        <Route path="/CollegeLogoutFromAllDevices" element={<CollegeLogoutFromAllDevices />} />
        <Route path="/college/purchase-credits" element={<CollegeCreditPurchase />} />
        <Route path="/college/subscription-managed" element={<SalesTeamManagedPage />} />
        <Route path="/college/transaction-history" element={<CollegeTransactionHistory />} />
        <Route path="/college/enterprise-purchase/:purchaseId" element={<EnterprisePurchasePage />} />
        <Route path="/college/student-management" element={<CollegeStudentManagement />} />
        <Route path="/college/certificates" element={<CollegeCertificateManagement />} />
        <Route path="/college/github" element={<CollegeGitHubDashboard />} />
        <Route path="/institutional-impact" element={<InstitutionalImpact />} />

        {/* Certificate Verification (Public Route - No Login Required) */}
        <Route path="/verify/:token" element={<CertificateVerification />} />

        {/* ── Tenant Student Dashboard (new, isolated experience) ─────── */}
        <Route
          path="/dashboard"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <TenantDashboardHome />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/student/dashboard"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <TenantDashboardHome />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/student/certifications"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <MyCertifications />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />

        {/* Profile pages — wrapped in TenantDashboardLayout for tenant students */}
        <Route
          path="/profile"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <CleanProfile />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/edit-profile"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <EditProfile />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />

        {/* Learning routes for tenant students — ALL wrapped in TenantDashboardLayout */}
        <Route
          path="/techgrid"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <TechGrid />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/practice/:technology"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <LevelTree />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/questions/:technology/:level"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <QuestionPage />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/levelclearance"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <LevelClearance />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/launchpadpage"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <LaunchpadPage />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/skillgenlaunchpadlevels"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <SkillgenLaunchpadLevels />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/skillgen-questions"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <SkillgenLaunchpadQuestionPage />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/skillgenlaunchpadclearance"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <SkillgenLaunchpadLevelClearance />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/topicexplorer"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <LaunchpadReader />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/compiler"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <Compiler />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/notes"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <Notes />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/tsgmynotes"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <TSGMyNotes />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/tsgnotes"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <TSGNotes />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/student/tsg"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <TenantTSG />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/student/tsg/explain"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <ExplainPage mode="tenant" />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/student/tsg/resume-review"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <ResumeReviewPage mode="tenant" />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/student/tsg/interview"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <InterviewPage mode="tenant" />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/student/tsg/code-review"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <CodeReviewPage mode="tenant" />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/student/tsg/skill-test"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <SkillTestPage mode="tenant" />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/student/tsg/project-builder"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <ProjectBuilderPage mode="tenant" />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/student/tsg/:intent"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <TenantTSGPromptPage />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/bootcamp"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <BootcampSelector />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        {/* Multi-enrollment routes — uses TenantBootcampEnrollment (student-specific form) */}
        <Route
          path="/bootcamp/enrollment"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <TenantBootcampEnrollment />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/bootcamp/my-bootcamps"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <MyBootcamps />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/student/github"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <TenantGitHubProfile />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/bootcamp-selector"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <Bootcamp1 />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/bootcampselector"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <Bootcamp1 />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/thankyou"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <ThankYouPage />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/student/mockinterviewhome"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <TenantMockInterviewHome />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/student/mockinterview/coding"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <CodingInterviewPage mode="tenant" />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/student/mockinterview/arithmetic"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <MockInterviewGrid mode="tenant" />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/student/mockinterview/technical"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <TechnicalInterviewRoom mode="tenant" />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/student/mockinterview/hr"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <HRInterviewRoom mode="tenant" />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/student/mockinterview/time_management"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <Timemanagequestionsmock />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/student/mock-test/:topicTitle"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <MockTestPage mode="tenant" />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/student/mock-result"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <MockResultsPage mode="tenant" />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/interview-result"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <MockInterviewResult mode="tenant" />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/mockinterview/result"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <MockInterviewResult mode="tenant" />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/student/practice/:technology"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <LevelTree />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/student/skillgenlaunchpadlevels"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <SkillgenLaunchpadLevels />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/student/skillgen-questions"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <SkillgenLaunchpadQuestionPage />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/student/explore"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <TenantTechExplorerHome />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/student/explore/:slug"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <TenantTechnologyDetail />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/student/launch"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <TenantLaunchpadHome />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/student/launch/:track/:subtopic"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <TenantLaunchpadContent />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/mockinterviewpayments"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <MockInterviewPayments />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/mockinterviewpayments_history"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <MockInterviewMyPaymentsHistory />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/mockinterview/result"
          element={
            <MockInterviewResult />
          }
        />
        <Route
          path="/interview-result"
          element={
            <MockInterviewResult />
          }
        />
        <Route
          path="/bootcamp1"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <Bootcamp1 />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        {/* ── Bootcamp roles dashboard routes ── */}
        <Route
          path="/individual-roles"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <IndividualRoles />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/team-roles"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <TeamRoles />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/team-leaderboard"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <TeamLeaderboardPage />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/role-dashboard"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <RoleDashboard />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/individual-role-viewer"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <IndividualRoleViewer />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/team-role-replica"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <TeamRoleReplica />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/bootcamp/certificate"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <CertificatePage />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/certificate-preview"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <CertificatePreview />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/techbunchuserdashboard"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <Techbunchuserdashboard />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/techuserdashboard"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <Technologyuserdashboard />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/certifieduserdashboard"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <Certifieduserdashboard />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/support-student"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <SupportStudent />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/techmcq"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <TopicExplorer />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />

        {/* Password reset (needed on tenant domain) */}
        <Route path="/collegeforgotpass" element={<Collegeforgotpassword />} />
        <Route path="/collegereset/:token" element={<Collegeresetpassword />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />

        {/* Support */}
        <Route path="/support-college" element={<SupportCollege />} />

        {/* College home (after "Go to Dashboard") */}
        <Route path="/collegehome/:username" element={<CollegeHomePage />} />
        <Route
          path="/college-challenge"
          element={
            <CollegeChallengeGuard>
              <CollegeChallenge />
            </CollegeChallengeGuard>
          }
        />

        {/* Bootcamp roles-track dashboard — redirect to student dashboard */}
        <Route
          path="/dashboard"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <TenantDashboardHome />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />

        {/* Subscription/payment page — safety net if any component redirects here */}
        <Route
          path="/skillgenpayments"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <SubscriptionPlans />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />

        {/* Elite Technology Mastery */}
        <Route
          path="/elite-mastery"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <EliteTechnologyMastery />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />
        <Route
          path="/elite-mastery/:tech"
          element={
            <TenantStudentAuthGuard>
              <TenantDashboardLayout>
                <EliteTechnologyDetail />
              </TenantDashboardLayout>
            </TenantStudentAuthGuard>
          }
        />

        {/* Fallback → portal home */}
        <Route path="*" element={<TenantPortalHome />} />
      </Routes>
    );
  }

  // ── PUBLIC / MAIN DOMAIN ROUTES ─────────────────────────────────────
 return (
 <Routes>
 <Route path="/" element={<Home />} />
 <Route path="/collegeabout" element={<CollegeDashboard />} />
 <Route path="/college-analytics" element={<CollegeAnalyticsDashboard />} />
 {/* Tenant portal routes — redirect to collegesignup if accessed from main domain */}
 <Route path="/admin/login" element={<CollegeRegister />} />
 <Route path="/student/login" element={<Login />} />
 <Route path="/techmcq" element={<TopicExplorer/>} />
 <Route path="/contact" element={<Contact />} />
 <Route path="/register" element={<Register />} />
 <Route path="/login" element={<Login />} />
 <Route path="/about" element={<About />} />
 <Route path="/book-demo" element={<PublicBookDemo />} />
 <Route path="/reset-password/:token" element={<ResetPassword />} />
 <Route path="/forgot-password" element={<ForgotPassword />} />
 <Route path="/account-locked" element={<LockedAccount />} />
{/* <Route path="/support" element={<Support />} /> */}
 <Route path="/dashboard" element={<Dashboard />} />
 <Route path="/profile" element={<CleanProfile />} />
 <Route path="/practice/:technology" element={<LevelTree />} />
 <Route path="/leaderboard" element={<Leaderboard />} />
 <Route path="/levelclearance" element={<LevelClearance />} />
 <Route path="/code-hunt" element={<FlipCardList />} />
 <Route path="/admin" element={<AdminDashboard />} />
 <Route path="/techgrid" element={<TechGrid />} />
 <Route path="/python" element={<Python />} />
 <Route path="/questions/:technology/:level" element={<QuestionPage />} />
 <Route path="/level1" element={<HuntLevel1 />} />
 <Route path="/level2" element={<HuntLevel2 />} />
 <Route path="/level3" element={<HuntLevel3 />} />
 <Route path="/usertype" element={<UserTypeSelector />} />
 {/* TSG Nexus for colleges. /tsg-collab is the official URL;
     the old /college-intro path only redirects to it (query + hash kept). */}
 <Route path="/tsg-collab" element={<CollegeIntroPage />} />
 <Route path="/college-intro" element={<CollegeIntroRedirect />} />
 {/* TSG Nexus Product Engineering. /tsg-product-engg is the official URL;
     the old /student-intro path only redirects to it (query + hash kept). */}
 <Route path="/tsg-product-engg" element={<StudentIntroPage />} />
 <Route path="/student-intro" element={<StudentIntroRedirect />} />
 {/* Comparison pages — registered in src/seo/routes.config.js; content in
     BasicComponents/compare/compareContent.js. */}
 <Route path="/tsg-vs-youtube" element={<TsgVsYoutube />} />
 <Route path="/tsg-vs-edtech-platforms" element={<TsgVsEdtech />} />
 <Route path="/tsg-vs-coaching-centres" element={<TsgVsCoaching />} />
 <Route path="/collegesignup" element={<CollegeRegister />} />
 <Route path="/skillgenlaunchpad" element={<SkillgenLaunchpad />} />

 {/* NextGen Builder Public Routes */}
 <Route path="/nextgen-builder/register" element={<NextGenAuthProvider><NextGenAuthThemeProvider><NextGenRegister /></NextGenAuthThemeProvider></NextGenAuthProvider>} />
 <Route path="/nextgen-builder/login" element={<NextGenAuthProvider><NextGenAuthThemeProvider><NextGenLogin /></NextGenAuthThemeProvider></NextGenAuthProvider>} />
 <Route path="/nextgen-builder/forgot-password" element={<NextGenAuthProvider><NextGenAuthThemeProvider><NextGenForgotPassword /></NextGenAuthThemeProvider></NextGenAuthProvider>} />
 <Route path="/nextgen-builder/reset-password/:token" element={<NextGenAuthProvider><NextGenAuthThemeProvider><NextGenResetPassword /></NextGenAuthThemeProvider></NextGenAuthProvider>} />
        {/* Email verification. Two entry points, one page: the notice screen
            the user lands on after registering or after a blocked sign-in, and
            the tokenised link from the email itself. Both are public — an
            unverified builder has no session by design. */}
        <Route path="/nextgen-builder/verify-email" element={<NextGenAuthProvider><NextGenAuthThemeProvider><NextGenVerifyEmail /></NextGenAuthThemeProvider></NextGenAuthProvider>} />
        <Route path="/nextgen-builder/verify-email/:token" element={<NextGenAuthProvider><NextGenAuthThemeProvider><NextGenVerifyEmail /></NextGenAuthThemeProvider></NextGenAuthProvider>} />
        {/* Unsubscribe is reached from a link in an email, so it must work with
            no session: the signed token in the URL is the authorisation. */}
        <Route path="/nextgen-builder/notifications/unsubscribe" element={<NextGenAuthProvider><NextGenAuthThemeProvider><NextGenUnsubscribe /></NextGenAuthThemeProvider></NextGenAuthProvider>} />

 {/* NextGen Builder Protected Routes */}
 <Route path="/nextgen-builder/dashboard" element={<NextGenAuthProvider><NextGenLayout><NextGenDashboard /></NextGenLayout></NextGenAuthProvider>} />
 <Route path="/nextgen-builder/settings/notifications" element={<NextGenAuthProvider><NextGenLayout><NextGenNotificationSettings /></NextGenLayout></NextGenAuthProvider>} />
 {/* Per-version detail page — the "Open Dashboard" button on each version
     card in NextGenDashboard navigates here with the version's numeric id.
     The param is named :versionId (not :id) to match NextGenProjectVersion's
     existing useParams() destructure without touching that file. */}
 <Route path="/nextgen-builder/dashboard/project/:versionId" element={<NextGenAuthProvider><NextGenLayout><NextGenProjectVersion /></NextGenLayout></NextGenAuthProvider>} />
 <Route path="/nextgen-builder/profile" element={<NextGenAuthProvider><NextGenLayout><NextGenProfile /></NextGenLayout></NextGenAuthProvider>} />
 <Route path="/nextgen-builder/enrollment" element={<NextGenAuthProvider><NextGenLayout><NextGenEnrollment /></NextGenLayout></NextGenAuthProvider>} />
 <Route path="/nextgen-builder/purchase" element={<NextGenAuthProvider><NextGenLayout><NextGenPurchase /></NextGenLayout></NextGenAuthProvider>} />
 <Route path="/nextgen-builder/payment/:planId" element={<NextGenAuthProvider><NextGenLayout><NextGenPayment /></NextGenLayout></NextGenAuthProvider>} />
 <Route path="/nextgen-builder/payments" element={<NextGenAuthProvider><NextGenLayout><NextGenPayments /></NextGenLayout></NextGenAuthProvider>} />
 {/* Feature-based billing: per-feature credits, cart, checkout, orders */}
 <Route path="/nextgen-builder/plans" element={<NextGenAuthProvider><NextGenPaidFeatureGuard><NextGenLayout><NextGenFeaturePlans /></NextGenLayout></NextGenPaidFeatureGuard></NextGenAuthProvider>} />
 <Route path="/nextgen-builder/cart" element={<NextGenAuthProvider><NextGenPaidFeatureGuard><NextGenLayout><NextGenCart /></NextGenLayout></NextGenPaidFeatureGuard></NextGenAuthProvider>} />
 <Route path="/nextgen-builder/checkout" element={<NextGenAuthProvider><NextGenPaidFeatureGuard><NextGenLayout><NextGenCheckout /></NextGenLayout></NextGenPaidFeatureGuard></NextGenAuthProvider>} />
 <Route path="/nextgen-builder/orders/:orderId" element={<NextGenAuthProvider><NextGenPaidFeatureGuard><NextGenLayout><NextGenOrderDetail /></NextGenLayout></NextGenPaidFeatureGuard></NextGenAuthProvider>} />
 <Route path="/nextgen-builder/refer" element={<NextGenAuthProvider><NextGenLayout><NextGenReferral /></NextGenLayout></NextGenAuthProvider>} />
 <Route path="/nextgen-builder/wallet" element={<NextGenAuthProvider><NextGenLayout><NextGenWallet /></NextGenLayout></NextGenAuthProvider>} />
 <Route path="/nextgen-builder/support" element={<NextGenAuthProvider><NextGenLayout><SupportNextGen /></NextGenLayout></NextGenAuthProvider>} />

 {/* ── NextGen Builder / TSG Nexus — reused learner modules ──────────────
     AI Tutor, Elite Mastery and Mock Interview are NOT reimplemented here.
     Every route below renders the SAME components already live in the main
     learner app (ExplainPage, InterviewPage, EliteTechnologyMastery,
     MockInterviewHome, etc. — see imports above), reusing their existing
     APIs, services and business logic unmodified. `mode="tenant"` reuses
     the existing external-gating bypass (the same one TenantDashboard uses)
     since access here is gated by NextGenPaidFeatureGuard instead of the
     learner subscription paywall. Since the feature-based billing launch
     (26-Aug-2026) these routes only require a signed-in builder: each
     feature has a free allowance and its own paid credits, enforced
     server-side — see NextGenPaidFeatureGuard. */}

 {/* AI Tutor */}
 <Route path="/nextgen-builder/ai-tutor" element={<NextGenAuthProvider><NextGenPaidFeatureGuard><NextGenLayout><NextGenAITutorHub /></NextGenLayout></NextGenPaidFeatureGuard></NextGenAuthProvider>} />
 <Route path="/nextgen-builder/ai-tutor/notes" element={<NextGenAuthProvider><NextGenPaidFeatureGuard><NextGenLayout><TSG mode="tenant" /></NextGenLayout></NextGenPaidFeatureGuard></NextGenAuthProvider>} />
 <Route path="/nextgen-builder/ai-tutor/explain" element={<NextGenAuthProvider><NextGenPaidFeatureGuard><NextGenLayout><ExplainPage mode="tenant" /></NextGenLayout></NextGenPaidFeatureGuard></NextGenAuthProvider>} />
 <Route path="/nextgen-builder/ai-tutor/resume-review" element={<NextGenAuthProvider><NextGenPaidFeatureGuard><NextGenLayout><ResumeReviewPage mode="tenant" /></NextGenLayout></NextGenPaidFeatureGuard></NextGenAuthProvider>} />
 <Route path="/nextgen-builder/ai-tutor/interview" element={<NextGenAuthProvider><NextGenPaidFeatureGuard><NextGenLayout><InterviewPage mode="tenant" /></NextGenLayout></NextGenPaidFeatureGuard></NextGenAuthProvider>} />
 <Route path="/nextgen-builder/ai-tutor/code-review" element={<NextGenAuthProvider><NextGenPaidFeatureGuard><NextGenLayout><CodeReviewPage mode="tenant" /></NextGenLayout></NextGenPaidFeatureGuard></NextGenAuthProvider>} />
 <Route path="/nextgen-builder/ai-tutor/skill-test" element={<NextGenAuthProvider><NextGenPaidFeatureGuard><NextGenLayout><SkillTestPage mode="tenant" /></NextGenLayout></NextGenPaidFeatureGuard></NextGenAuthProvider>} />
 <Route path="/nextgen-builder/ai-tutor/project-builder" element={<NextGenAuthProvider><NextGenPaidFeatureGuard><NextGenLayout><ProjectBuilderPage mode="tenant" /></NextGenLayout></NextGenPaidFeatureGuard></NextGenAuthProvider>} />
 <Route path="/nextgen-builder/ai-tutor/:intent" element={<NextGenAuthProvider><NextGenPaidFeatureGuard><NextGenLayout><TSGPromptPage mode="tenant" /></NextGenLayout></NextGenPaidFeatureGuard></NextGenAuthProvider>} />

 {/* Elite Mastery */}
 <Route path="/nextgen-builder/elite-mastery" element={<NextGenAuthProvider><NextGenPaidFeatureGuard><NextGenLayout><EliteTechnologyMastery mode="tenant" /></NextGenLayout></NextGenPaidFeatureGuard></NextGenAuthProvider>} />
 <Route path="/nextgen-builder/elite-mastery/:tech" element={<NextGenAuthProvider><NextGenPaidFeatureGuard><NextGenLayout><EliteTechnologyDetail mode="tenant" /></NextGenLayout></NextGenPaidFeatureGuard></NextGenAuthProvider>} />

 {/* Mock Interview */}
 <Route path="/nextgen-builder/mock-interview" element={<NextGenAuthProvider><NextGenPaidFeatureGuard><NextGenLayout><MockInterviewHome mode="tenant" /></NextGenLayout></NextGenPaidFeatureGuard></NextGenAuthProvider>} />
 <Route path="/nextgen-builder/mock-interview/coding" element={<NextGenAuthProvider><NextGenPaidFeatureGuard><NextGenLayout><CodingInterviewPage mode="tenant" /></NextGenLayout></NextGenPaidFeatureGuard></NextGenAuthProvider>} />
 <Route path="/nextgen-builder/mock-interview/arithmetic" element={<NextGenAuthProvider><NextGenPaidFeatureGuard><NextGenLayout><MockInterviewGrid mode="tenant" /></NextGenLayout></NextGenPaidFeatureGuard></NextGenAuthProvider>} />
 <Route path="/nextgen-builder/mock-interview/technical" element={<NextGenAuthProvider><NextGenPaidFeatureGuard><NextGenLayout><TechnicalInterviewRoom mode="tenant" /></NextGenLayout></NextGenPaidFeatureGuard></NextGenAuthProvider>} />
 <Route path="/nextgen-builder/mock-interview/hr" element={<NextGenAuthProvider><NextGenPaidFeatureGuard><NextGenLayout><HRInterviewRoom mode="tenant" /></NextGenLayout></NextGenPaidFeatureGuard></NextGenAuthProvider>} />
 <Route path="/nextgen-builder/mock-interview/time_management" element={<NextGenAuthProvider><NextGenPaidFeatureGuard><NextGenLayout><Timemanagequestionsmock mode="tenant" /></NextGenLayout></NextGenPaidFeatureGuard></NextGenAuthProvider>} />
 <Route path="/nextgen-builder/mock-interview/mock-test/:topicTitle" element={<NextGenAuthProvider><NextGenPaidFeatureGuard><NextGenLayout><MockTestPage mode="tenant" /></NextGenLayout></NextGenPaidFeatureGuard></NextGenAuthProvider>} />
 <Route path="/nextgen-builder/mock-interview/result" element={<NextGenAuthProvider><NextGenPaidFeatureGuard><NextGenLayout><MockInterviewResult mode="tenant" /></NextGenLayout></NextGenPaidFeatureGuard></NextGenAuthProvider>} />
 <Route path="/nextgen-builder/mock-interview/mock-result" element={<NextGenAuthProvider><NextGenPaidFeatureGuard><NextGenLayout><MockResultsPage mode="tenant" /></NextGenLayout></NextGenPaidFeatureGuard></NextGenAuthProvider>} />

 <Route path="/launchpad/:track" element={<TrackQuestionPage />} />
 <Route path="/mockinterviewpayments_history" element={<MockInterviewMyPaymentsHistory />} />
 <Route path="/mockinterviewpayments" element={<MockInterviewPayments />} />
 <Route path="/mockinterviewhome" element={<MockInterviewHome />} />
 <Route path="/premium-interview" element={<PremiumInterviewView userId={localStorage.getItem('user_id')} technology="Java" experience="Junior" interviewType="Technical" />} />
 {/* <Route path="/discount-interview" element={<DiscountInterviewView userId={localStorage.getItem('user_id')} technology="Java" experience="Junior" />} /> */}
 <Route path="/coding-interview" element={<CodingInterviewView userId={localStorage.getItem('user_id')} technology="Java" experience="Junior" />} />
        <Route path="/mockinterview/coding" element={<CodingInterviewPage />} />
        <Route path="/mockinterview/arithmetic" element={<MockInterviewGrid />} />
        <Route path="/mockinterview/technical" element={<TechnicalInterviewRoom />} />
        <Route path="/mockinterview/hr" element={<HRInterviewRoom />} />
        <Route path="/mockinterview/time_management" element={<Timemanagequestionsmock />} />
        <Route path="/mockinterview/mock-test/:topicTitle" element={<MockTestPage />} />
        <Route path="/mockinterview/mock-result" element={<MockResultsPage />} />
        <Route path="/interview-result" element={<MockInterviewResult />} />
 <Route path="/collegehome/:username" element={<CollegeHomePage />} />
 <Route path="/CollegeViewProfile" element={<CollegeViewProfile />} />
 <Route path="/CollegeEditProfile" element={<CollegeEditProfile />} />
 <Route path="/TotalStudents" element={<CollegeTotalStudents />} />
 <Route path="/StudentsProgress" element={<CollegeStudentsProgress />} />
 <Route path="/TopPerformers" element={<CollegeTopPerformers />} />
 <Route path="/CollegeFAQs" element={<CollegeFAQs />} />
 <Route path="/CollegeFeedback" element={<CollegeFeedbackInbox />} />
 <Route path="/CollegeLogoutFromThisDevice" element={<CollegeLogoutFromThisDevice />} />
 <Route path="/CollegeLogoutFromAllDevices" element={<CollegeLogoutFromAllDevices />} />
 <Route path="/college/purchase-credits" element={<CollegeCreditPurchase />} />
 <Route path="/college/subscription-managed" element={<SalesTeamManagedPage />} />
 <Route path="/college/transaction-history" element={<CollegeTransactionHistory />} />
 <Route path="/college/enterprise-purchase/:purchaseId" element={<EnterprisePurchasePage />} />
 <Route path="/college/student-management" element={<CollegeStudentManagement />} />
 <Route path="/college/certificates" element={<CollegeCertificateManagement />} />
 <Route path="/college/github" element={<CollegeGitHubDashboard />} />
 <Route path="/institutional-impact" element={<InstitutionalImpact />} />
 <Route path="/edit-profile" element={<EditProfile />} />
 <Route path="/notes" element={<Notes />} />
 <Route path='/tsgmynotes' element={<TSGMyNotes />} />
 <Route path='/tsgnotes' element={<TSGNotes />} />
 <Route path="/contest" element={<ContestPage />} />
 <Route path="/collegelogin" element={<Collegelogin />} />
 <Route path="/launchpadpage" element={<LaunchpadPage />} />
 <Route path="/skillgen-questions" element={<SkillgenLaunchpadQuestionPage />} />
 <Route path="/skillgenlaunchpadlevels" element={<SkillgenLaunchpadLevels />} />
 <Route path="/skillgenlaunchpadclearance" element={<SkillgenLaunchpadLevelClearance />} />
 <Route path="/supporttickets" element={<SupportTicketDashboard />} />
 <Route path="/adminusersdashboard" element={<AdminUsersDashboard />} />
 <Route path="/admincollegesdashboard" element={<AdminCollegesDashboard />} />
 <Route path="/admin-console/lead-ambassadors" element={<LeadAmbassadorDashboard />} />
 <Route path="/admintechgrid" element={<AdminTechnologySelector />} />
 <Route path="/admin/manage-questions/:technology" element={<AdminQuestionManager />} />
 <Route path="/skillgenpayment" element={<SkillGenPaymentPage />} />

 <Route path="/chatbot" element={<SkillgenproCompiler chatBotRef={chatBotRef} setShowChat={setShowChat} />} />
 <Route path="/topicexplorer" element={<LaunchpadReader />} />
 <Route path="/tech-tree-reader" element={<TechTreeReader />} />
 <Route path="/elite-mastery" element={<EliteTechnologyMastery />} />
 <Route path="/elite-mastery/:tech" element={<EliteTechnologyDetail />} />
   <Route path="/nextgen/projects"               element={<ProjectsList />} />
   <Route path="/nextgen/projects/:id"           element={<ProjectDetails />} />
   <Route path="/nextgen/versions/:id"           element={<VersionDetails />} />
   <Route path="/nextgen/roles"                  element={<RolesManagement />} />
   <Route path="/nextgen/templates"              element={<TemplatesManagement />} />
   <Route path="/nextgen/github-access"          element={<GitHubAccess />} />
   <Route path="/nextgen/finance"                element={<NextGenFinance />} />
   {/* Full-screen Elite content workspace (deliberately outside SuperAdminLayout: dedicated reading/editing surface) */}
   <Route path="/superadmin/elite-technologies/content/:contentId" element={<EliteContentWorkspace />} />
   <Route element={<SuperAdminLayout />}>
     <Route path="/superadmin/tenants"                        element={<TenantManagement />} />
     <Route path="/superadmin/lead-ambassadors"               element={<LeadAmbassadorDashboard />} />
     <Route path="/superadmin/tee-gee-leads"                  element={<TeeGeeLeads />} />
     <Route path="/superadmin/elite-technologies"              element={<EliteTechnologyManager />} />
     <Route path="/superadmin/feature-pricing"                 element={<FeaturePricingAdmin />} />
     <Route path="/superadmin/push-nudges"                     element={<PushNudgesAdmin />} />
     <Route path="/superadmin/nexus-billing"                   element={<NexusBillingAdmin />} />
     <Route path="/superadmin/announcements"                   element={<AnnouncementsAdmin />} />
     <Route path="/bulkemailing"                              element={<BulkEmailing />} />
     <Route path="/superpayments"                             element={<SuperPayments />} />
   </Route>

 {/* Finance + CRM — wrapped in AdminLayout (purple dashboard sidebar) */}
 <Route element={<AdminLayout />}>
   <Route path="/superadmin/finance/enterprise-payments"    element={<EnterprisePaymentsAdmin />} />

   {/* CRM Module — Super Admin only */}
   <Route path="/superadmin/crm"                            element={<CRMDashboard />} />
   <Route path="/superadmin/crm/contacts"                   element={<CRMContacts />} />
   <Route path="/superadmin/crm/bulk-upload"                element={<CRMBulkUpload />} />
   <Route path="/superadmin/crm/sms"                        element={<CRMSMS />} />
   <Route path="/superadmin/crm/whatsapp"                   element={<CRMWhatsApp />} />
   <Route path="/superadmin/crm/voice"                      element={<CRMVoiceCalling />} />
   <Route path="/superadmin/crm/demos"                      element={<CRMScheduleDemo />} />
   <Route path="/superadmin/crm/calendar"                   element={<CRMDemoCalendar />} />
   <Route path="/superadmin/crm/history"                    element={<CRMCommunicationHistory />} />
   <Route path="/superadmin/crm/analytics"                  element={<CRMAnalytics />} />
   <Route path="/superadmin/crm/settings"                   element={<CRMSettings />} />
 </Route>
 <Route path="/bootcamp" element={< BootcampSelector/>} />
 <Route path="/bootcamp-selector" element={<Bootcamp1 />} />
 <Route path="/bootcampselector" element={<Bootcamp1 />} />
 <Route path="/thankyou" element={<ThankYouPage />} />

 <Route path="/languagegrid" element={<LanguageGrid />} />
 <Route path="/practices/:language/:level" element={<PracticeLevelPage />} />
 <Route path="/practices/:language/:level/:questionId" element={<SolveCodingQuestionPage />} />
 <Route path="/compiler" element={<Compiler />} />
 <Route path="/tsg" element={<TSG />} />
 <Route path="/tsg/:intent" element={<TSGPromptPage />} />
 <Route path="/tsg/explain" element={<ExplainPage />} />
 <Route path="/tsg/resume-review" element={<ResumeReviewPage />} />
 <Route path="/tsg/interview" element={<InterviewPage />} />
 <Route path="/tsg/code-review" element={<CodeReviewPage />} />
 <Route path="/tsg/skill-test" element={<SkillTestPage />} />
 <Route path="/tsg/project-builder" element={<ProjectBuilderPage />} />
 <Route path="/features" element={<FeaturesOverview />} />
 <Route path="/collegeforgotpass" element={<Collegeforgotpassword />} />
 <Route path="/collegereset/:token" element={<Collegeresetpassword />} />
 <Route path="/launch" element={<LaunchpadHome />} />
 <Route path="/launch/:track/:subtopic" element={<TopicContent />} />
 <Route path="/explore" element={<TechExplorerHome />} />
 <Route path="/explore/:slug" element={<TechnologyDetail />} />
 <Route path="/support-student" element={<SupportStudent />} />
 <Route path="/support-college" element={<SupportCollege />} />
 <Route path="/landingpage1" element={<LandingPage1 />} />
 <Route path="/landingpage2" element={<LandingPage2 />} />
 <Route path="/bootcamp1" element={<Bootcamp1/>} />
 <Route path="/bootcamp/enrollment" element={<Bootcamp1/>} />
 <Route path="/bootcamp/my-bootcamps" element={
 <SubscriptionGuard>
 <MyBootcamps />
 </SubscriptionGuard>
 } />
 <Route path="/bootcamp/dashboard" element={
 <SubscriptionGuard>
 <UnifiedBootcampDashboard />
 </SubscriptionGuard>
 } />
 {/* Bootcamp Dashboards - Using existing working dashboards */}
 <Route path="/techuserdashboard" element={
 <SubscriptionGuard>
 <Technologyuserdashboard />
 </SubscriptionGuard>
 } />
 <Route path="/techbunchuserdashboard" element={
 <SubscriptionGuard>
 <Techbunchuserdashboard />
 </SubscriptionGuard>
 } />
 <Route path="/certifieduserdashboard" element={
 <SubscriptionGuard>
 <Certifieduserdashboard />
 </SubscriptionGuard>
 } />
 <Route path="/adminroles" element={<Adminroles />} />
 <Route path="/admin1" element={<AdminProjectTeams />} />
 <Route path="/individual-roles" element={
 <SubscriptionGuard>
 <IndividualRoles />
 </SubscriptionGuard>
 } />
 <Route path="/team-roles" element={
 <SubscriptionGuard>
 <TeamRoles />
 </SubscriptionGuard>
 } />
 <Route path="/team-leaderboard" element={
 <SubscriptionGuard>
 <TeamLeaderboardPage />
 </SubscriptionGuard>
 } />
 <Route path="/payment" element={<PaymentPage />} />
 <Route path="/payment-success" element={<PaymentSuccess />} />
 <Route path="/subscription-plans" element={<SubscriptionPlans />} />
 <Route path="/role-dashboard" element={<RoleDashboard />} />
 <Route path="/individual-role-viewer" element={<IndividualRoleViewer />} />
 <Route path="/team-role-replica" element={<TeamRoleReplica />} />
 <Route path="/bootcamp/certificate" element={<CertificatePage />} />
 <Route path="/certificate-demo" element={<CertificateSystemDemo />} />
 <Route path="/certificate-preview" element={<CertificatePreview />} />
 <Route path="/voice-training" element={<VoiceTrainingPage />} />

 {/* Legal Pages */}
 <Route path="/terms-of-service" element={<TermsOfService />} />
 <Route path="/privacy-policy" element={<PrivacyPolicy />} />

 {/* Google Play requires a deletion URL reachable without installing the app. */}
 <Route path="/account-deletion" element={<AccountDeletion />} />
 <Route path="/delete-account" element={<AccountDeletion />} />

 {/* Short aliases. The mobile app and printed material link to these. */}
 <Route path="/privacy" element={<PrivacyPolicy />} />
 <Route path="/terms" element={<TermsOfService />} />

 {/* Certificate Verification (Public Route - No Login Required) */}
 <Route path="/verify/:token" element={<CertificateVerification />} />

  {/* Universal subscription/payment route - works for all premium features */}
  <Route path="/skillgenpayments" element={<SubscriptionPlans />} />

  <Route path="*" element={<NotFound />} />
</Routes>
 );
};

// ✅ END of functional component
export default AppRoutes;